// =============================================================================
// FILE:
// frontend/src/ipe/pms/components/site_store_dashboard/SiteStoreExecutionDrawer.jsx
// =============================================================================

import React, {
  useState,
  useEffect
} from "react";

import {
  X,
  Maximize2,
  Minimize2,
  Package,
  Truck,
  Wrench,
  AlertTriangle,
  RotateCcw,
  Building2,
  Layers3,
  ClipboardList,
  Boxes
} from "lucide-react";

import SiteStoreIssueHistorySection
  from "./SiteStoreIssueHistorySection";

import SiteStoreConsumptionHistorySection
  from "./SiteStoreConsumptionHistorySection";

import MaterialMovementLedger
  from "./MaterialMovementLedger";

import {
  getMaterialMovementLedger
} from "../../services/materialMovementLedgerService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function SiteStoreExecutionDrawer({

  open,

  row,

  onClose

}) {

  // ===========================================================================
  // STATES
  // ===========================================================================

  const [maximized, setMaximized] =
    useState(false);

  const [ledgerRows, setLedgerRows] =
    useState([]);

  const [ledgerLoading, setLedgerLoading] =
    useState(false);


  // ===========================================================================
  // LOAD MATERIAL MOVEMENT LEDGER
  // ===========================================================================

  const loadLedger =
    async () => {

      try {

        if (!row) {
          return;
        }

        setLedgerLoading(true);

        const response =
          await getMaterialMovementLedger({

            project_id:
              row.project_id,

            material_id:
              row.material_id
          });

        setLedgerRows(
          response?.data || []
        );

      } catch (err) {

        console.error(err);

      } finally {

        setLedgerLoading(false);
      }
    };


  // ===========================================================================
  // LOAD ON OPEN
  // ===========================================================================

  useEffect(() => {

    if (open && row) {

      loadLedger();
    }

  }, [open, row]);


  // ===========================================================================
  // RETURN
  // ===========================================================================

  if (!open || !row) {

    return null;
  }


  // ===========================================================================
  // SUMMARY CARD
  // ===========================================================================

  const renderSummaryCard = (

    title,
    value,
    color,
    icon

  ) => {

    return (

      <div
        style={{
          ...drawerStyles.summaryCard,
          borderTop:
            `4px solid ${color}`
        }}
      >

        <div style={drawerStyles.summaryCardTop}>

          <div style={drawerStyles.summaryCardTitle}>

            {title}

          </div>

          <div
            style={{
              color
            }}
          >

            {icon}

          </div>

        </div>

        <div style={drawerStyles.summaryCardValue}>

          {value}

        </div>

      </div>
    );
  };


  // ===========================================================================
  // INFO CARD
  // ===========================================================================

  const renderInfoCard = (

    label,
    value,
    icon

  ) => {

    return (

      <div style={drawerStyles.infoCard}>

        <div style={drawerStyles.infoCardIcon}>

          {icon}

        </div>

        <div>

          <div style={drawerStyles.infoCardLabel}>

            {label}

          </div>

          <div style={drawerStyles.infoCardValue}>

            {value || "-"}

          </div>

        </div>

      </div>
    );
  };


  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (

    <>

      {/* ================================================================ */}
      {/* BACKDROP */}
      {/* ================================================================ */}

      <div
        style={drawerStyles.backdrop}
        onClick={onClose}
      />


      {/* ================================================================ */}
      {/* DRAWER */}
      {/* ================================================================ */}

      <div
        style={{
          ...drawerStyles.drawer,

          width:
            maximized
              ? "96vw"
              : "88vw"
        }}
      >

        <div style={drawerStyles.contentContainer}>


          {/* ============================================================ */}
          {/* HEADER */}
          {/* ============================================================ */}

          <div style={drawerStyles.header}>


            <div>

              <div style={drawerStyles.title}>

                Material Execution

              </div>

              <div style={drawerStyles.subtitle}>

                Enterprise Material Movement
                & Execution Visibility

              </div>


              <div style={drawerStyles.badgeRow}>

                <div style={drawerStyles.badgeBlue}>

                  {row.material_code || "N/A"}

                </div>

                <div style={drawerStyles.badgeGreen}>

                  STOCK:
                  {" "}
                  {row.current_stock_qty || 0}

                </div>

              </div>

            </div>


            <div
              style={{
                display: "flex",
                gap: 10
              }}
            >

              <button
                style={drawerStyles.iconBtn}
                onClick={() =>
                  setMaximized(
                    !maximized
                  )
                }
              >

                {
                  maximized
                    ? <Minimize2 size={18} />
                    : <Maximize2 size={18} />
                }

              </button>

              <button
                style={drawerStyles.iconBtn}
                onClick={onClose}
              >

                <X size={18} />

              </button>

            </div>

          </div>


          {/* ============================================================ */}
          {/* MATERIAL INFO */}
          {/* ============================================================ */}

          <div style={drawerStyles.section}>


            <div style={drawerStyles.sectionTitle}>

              Material Information

            </div>


            <div style={drawerStyles.infoGrid}>


              {
                renderInfoCard(
                  "Material",
                  row.material_name,
                  <Boxes size={18} />
                )
              }

              {
                renderInfoCard(
                  "Project",
                  row.project_name,
                  <Building2 size={18} />
                )
              }

              {
                renderInfoCard(
                  "Phase",
                  row.phase_name,
                  <Layers3 size={18} />
                )
              }

              {
                renderInfoCard(
                  "Task",
                  row.task_name,
                  <ClipboardList size={18} />
                )
              }

            </div>

          </div>


          {/* ============================================================ */}
          {/* EXECUTION SUMMARY */}
          {/* ============================================================ */}

          <div style={drawerStyles.section}>


            <div style={drawerStyles.sectionTitle}>

              Execution Summary

            </div>


            <div style={drawerStyles.summaryGrid}>


              {
                renderSummaryCard(

                  "Current Stock",

                  row.current_stock_qty || 0,

                  "#2563eb",

                  <Package size={18} />
                )
              }

              {
                renderSummaryCard(

                  "Issued Qty",

                  row.issued_qty || 0,

                  "#9333ea",

                  <Truck size={18} />
                )
              }

              {
                renderSummaryCard(

                  "Consumed Qty",

                  row.consumed_qty || 0,

                  "#0891b2",

                  <Wrench size={18} />
                )
              }

              {
                renderSummaryCard(

                  "Pending Qty",

                  row.pending_consumption_qty || 0,

                  "#ea580c",

                  <AlertTriangle size={18} />
                )
              }

              {
                renderSummaryCard(

                  "Returned Qty",

                  row.returned_qty || 0,

                  "#16a34a",

                  <RotateCcw size={18} />
                )
              }

              {
                renderSummaryCard(

                  "Scrap Qty",

                  row.scrap_qty || 0,

                  "#dc2626",

                  <AlertTriangle size={18} />
                )
              }

            </div>

          </div>


          {/* ============================================================ */}
          {/* LIFECYCLE */}
          {/* ============================================================ */}

          <div style={drawerStyles.section}>


            <div style={drawerStyles.sectionTitle}>

              Material Lifecycle

            </div>


            <div style={drawerStyles.lifecycleWrap}>


              <div style={drawerStyles.lifecycleStep}>

                <div
                  style={{
                    ...drawerStyles.lifecycleDot,
                    background: "#2563eb"
                  }}
                />

                <div style={drawerStyles.lifecycleLabel}>

                  Reserved

                </div>

              </div>


              <div style={drawerStyles.lifecycleLine} />


              <div style={drawerStyles.lifecycleStep}>

                <div
                  style={{
                    ...drawerStyles.lifecycleDot,
                    background: "#9333ea"
                  }}
                />

                <div style={drawerStyles.lifecycleLabel}>

                  Issued

                </div>

              </div>


              <div style={drawerStyles.lifecycleLine} />


              <div style={drawerStyles.lifecycleStep}>

                <div
                  style={{
                    ...drawerStyles.lifecycleDot,
                    background: "#0891b2"
                  }}
                />

                <div style={drawerStyles.lifecycleLabel}>

                  Consumed

                </div>

              </div>


              <div style={drawerStyles.lifecycleLine} />


              <div style={drawerStyles.lifecycleStep}>

                <div
                  style={{
                    ...drawerStyles.lifecycleDot,
                    background: "#16a34a"
                  }}
                />

                <div style={drawerStyles.lifecycleLabel}>

                  Returned

                </div>

              </div>

            </div>

          </div>


          {/* ============================================================ */}
          {/* ISSUE HISTORY */}
          {/* ============================================================ */}

          <SiteStoreIssueHistorySection

            row={row}

          />


          {/* ============================================================ */}
          {/* CONSUMPTION HISTORY */}
          {/* ============================================================ */}

          <SiteStoreConsumptionHistorySection

            row={row}

          />


          {/* ============================================================ */}
          {/* LEDGER */}
          {/* ============================================================ */}

          <div style={drawerStyles.section}>


            <div style={drawerStyles.sectionTitle}>

              Material Movement Ledger

            </div>

            {
              ledgerLoading ? (

                <div style={drawerStyles.loadingBox}>

                  Loading movement ledger...

                </div>

              ) : (

                <MaterialMovementLedger

                  ledgerRows={ledgerRows}

                />

              )
            }

          </div>

        </div>

      </div>

    </>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const drawerStyles = {

  backdrop: {

    position: "fixed",

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,

    background:
      "rgba(15,23,42,0.45)",

    backdropFilter: "blur(2px)",

    zIndex: 999
  },

  drawer: {

    position: "fixed",

    top: 0,

    right: 0,

    height: "100vh",

    background: "#f8fafc",

    zIndex: 1000,

    overflowY: "auto",

    boxShadow:
      "-10px 0 40px rgba(0,0,0,0.18)"
  },

  contentContainer: {

    maxWidth: 1280,

    margin: "0 auto",

    padding: 24
  },

  header: {

    position: "sticky",

    top: 0,

    zIndex: 50,

    background: "#f8fafc",

    paddingBottom: 18,

    marginBottom: 26,

    display: "flex",

    justifyContent: "space-between",

    alignItems: "flex-start",

    borderBottom:
      "1px solid #e5e7eb"
  },

  title: {

    fontSize: 28,

    fontWeight: 800,

    color: "#111827"
  },

  subtitle: {

    marginTop: 6,

    fontSize: 14,

    color: "#64748b"
  },

  badgeRow: {

    display: "flex",

    gap: 10,

    marginTop: 14
  },

  badgeBlue: {

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "6px 12px",

    borderRadius: 30,

    fontSize: 12,

    fontWeight: 700
  },

  badgeGreen: {

    background: "#dcfce7",

    color: "#166534",

    padding: "6px 12px",

    borderRadius: 30,

    fontSize: 12,

    fontWeight: 700
  },

  iconBtn: {

    width: 40,

    height: 40,

    border: "1px solid #d1d5db",

    borderRadius: 10,

    background: "#ffffff",

    cursor: "pointer",

    display: "flex",

    alignItems: "center",

    justifyContent: "center"
  },

  section: {

    marginBottom: 32
  },

  sectionTitle: {

    fontSize: 18,

    fontWeight: 700,

    marginBottom: 16,

    color: "#111827"
  },

  infoGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",

    gap: 18
  },

  infoCard: {

    background: "#ffffff",

    border:
      "1px solid #e5e7eb",

    borderRadius: 16,

    padding: 18,

    display: "flex",

    alignItems: "center",

    gap: 14,

    boxShadow:
      "0 1px 3px rgba(0,0,0,0.04)"
  },

  infoCardIcon: {

    width: 42,

    height: 42,

    borderRadius: 12,

    background: "#eff6ff",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    color: "#2563eb"
  },

  infoCardLabel: {

    fontSize: 12,

    color: "#64748b",

    marginBottom: 4
  },

  infoCardValue: {

    fontSize: 15,

    fontWeight: 700,

    color: "#111827"
  },

  summaryGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(220px, 1fr))",

    gap: 18
  },

  summaryCard: {

    background: "#ffffff",

    borderRadius: 18,

    padding: 18,

    boxShadow:
      "0 2px 6px rgba(0,0,0,0.05)"
  },

  summaryCardTop: {

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  summaryCardTitle: {

    fontSize: 13,

    color: "#64748b",

    fontWeight: 600
  },

  summaryCardValue: {

    marginTop: 14,

    fontSize: 30,

    fontWeight: 800,

    color: "#111827"
  },

  lifecycleWrap: {

    background: "#ffffff",

    borderRadius: 18,

    padding: 26,

    border:
      "1px solid #e5e7eb",

    display: "flex",

    alignItems: "center",

    justifyContent: "space-between",

    gap: 12
  },

  lifecycleStep: {

    display: "flex",

    flexDirection: "column",

    alignItems: "center",

    minWidth: 120
  },

  lifecycleDot: {

    width: 18,

    height: 18,

    borderRadius: "50%"
  },

  lifecycleLabel: {

    marginTop: 10,

    fontSize: 13,

    fontWeight: 700,

    color: "#374151"
  },

  lifecycleLine: {

    flex: 1,

    height: 2,

    background: "#d1d5db"
  },

  loadingBox: {

    background: "#ffffff",

    padding: 20,

    borderRadius: 14,

    textAlign: "center",

    color: "#64748b",

    border:
      "1px solid #e5e7eb"
  }
};